/* eslint-disable react/no-unstable-nested-components */
import {
  AttributeEditor,
  Button,
  Input,
  Select,
} from '@cloudscape-design/components';
import useProjects from 'hooks/use-projects';
import PropTypes from 'prop-types';
import React from 'react';

import PartRefSelect from './PartRefSelect';

/**
 * Row editor for part references (project → part, possibly cross-project),
 * shared by a part's Link Parts and its BOM:
 * - `withQuantity` adds the per-unit quantity column (BOM). Rows are emitted
 *   in the shared `components` / `linkedParts` schema shapes; quantity stays
 *   a raw string here and is cast/validated by Yup on save.
 * - `isInternal` picks which projects are offered: `true` for internal only
 *   (internal parts' links, every BOM), `null` for all projects (a customer
 *   part may link tooling from its own or an internal project).
 * - `defaultProjectDocId` pre-fills new rows' project (Link Parts default to
 *   the current project; BOM rows start blank).
 * - `excludeTypes` / `excludePPartDocId` narrow the part picker.
 * - `sameProjectOnlyTypes` are offered only when the row's project is the
 *   current one (tools: the run modal's tool value has no project component,
 *   so a cross-project tool link could never be honoured).
 */
export default function PartRefEditor({
  rows,
  onChange,
  isInternal = null,
  withQuantity = false,
  defaultProjectDocId = null,
  excludeTypes = [],
  sameProjectOnlyTypes = [],
  excludePPartDocId = null,
  addButtonText,
  empty,
}) {
  const { projectOptions, isLoading: projectsLoading } = useProjects({ isInternal });

  const updateRow = (index, patch) => onChange(
    rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
  );

  const definition = [
    {
      label: isInternal ? 'Internal Project' : 'Project',
      control: (item, index) => (
        <Select
          // Fall back to the raw doc ID (like PartRefSelect) so a row whose
          // project isn't in the offered list doesn't render blank and
          // invite an accidental re-pick.
          selectedOption={
            projectOptions.find((option) => option.id === item.projectDocId)
            || (item.projectDocId
              ? { label: item.projectDocId, id: item.projectDocId }
              : null)
          }
          options={projectOptions}
          statusType={projectsLoading ? 'loading' : 'finished'}
          filteringType="auto"
          expandToViewport
          placeholder="Select a project"
          onChange={({ detail }) => updateRow(index, {
            projectDocId: detail.selectedOption.id,
            pPartDocId: null,
          })}
        />
      ),
    },
    {
      label: 'Part',
      control: (item, index) => {
        const isCurrentProject = item.projectDocId === defaultProjectDocId;
        return (
          <PartRefSelect
            projectDocId={item.projectDocId}
            pPartDocId={item.pPartDocId}
            excludeTypes={isCurrentProject
              ? excludeTypes
              : [...excludeTypes, ...sameProjectOnlyTypes]}
            excludePPartDocId={isCurrentProject ? excludePPartDocId : null}
            onChange={(pPartDocId) => updateRow(index, { pPartDocId })}
          />
        );
      },
    },
  ];
  if (withQuantity) {
    definition.push({
      label: 'Qty per unit',
      control: (item, index) => (
        <Input
          type="number"
          value={item.quantity != null ? String(item.quantity) : ''}
          onChange={({ detail }) => updateRow(index, { quantity: detail.value })}
        />
      ),
    });
  }

  return (
    <AttributeEditor
      items={rows}
      addButtonText={addButtonText}
      empty={empty}
      // Keep each row on one line, with an icon-only remove button instead
      // of the full-width text button.
      gridLayout={[{
        rows: [withQuantity ? [3, 4, 2] : [4, 5]],
        removeButton: { ownRow: false, width: 'auto' },
      }]}
      customRowActions={({ itemIndex }) => (
        <Button
          iconName="close"
          variant="icon"
          ariaLabel="Remove row"
          // Cloudscape buttons default to formAction="submit"; inside the
          // part modal's <form> this would save and close on every remove.
          formAction="none"
          onClick={() => onChange(rows.filter((row, i) => i !== itemIndex))}
        />
      )}
      onAddButtonClick={() => onChange([
        ...rows,
        {
          projectDocId: defaultProjectDocId,
          pPartDocId: null,
          ...(withQuantity ? { quantity: 1 } : {}),
        },
      ])}
      definition={definition}
    />
  );
}

PartRefEditor.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  isInternal: PropTypes.bool,
  withQuantity: PropTypes.bool,
  defaultProjectDocId: PropTypes.string,
  excludeTypes: PropTypes.arrayOf(PropTypes.string),
  sameProjectOnlyTypes: PropTypes.arrayOf(PropTypes.string),
  excludePPartDocId: PropTypes.string,
  addButtonText: PropTypes.string.isRequired,
  empty: PropTypes.node.isRequired,
};
